import React, { useMemo } from 'react';
import { DownloadIcon, MailIcon } from 'lucide-react';
import { policyMeta, policySections } from '../data/privacyPolicy';
import { PolicyContents } from '../components/legal/PolicyContents';
import { PolicySectionBody } from '../components/legal/PolicySectionBody';
import { BackToTop } from '../components/legal/BackToTop';
import { SiteFooter } from '../components/ui/SiteFooter';
import { useActiveSection } from '../hooks/useActiveSection';
const metaLinkStyles = 'inline-flex items-center gap-2 text-sm font-medium text-purple-700 underline underline-offset-4 decoration-purple-300 hover:text-purple-900 hover:decoration-purple-700 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory rounded';
export function PrivacyPolicyPage() {
  const sectionIds = useMemo(() => policySections.map(section => section.id), []);
  const activeId = useActiveSection(sectionIds);
  return <div className="min-h-screen bg-ivory font-sans text-charcoal selection:bg-purple-200 selection:text-charcoal" data-id="element-518" data-mp="29q1xxoxg7z49">
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 px-6 py-6 flex justify-between items-center max-w-7xl mx-auto bg-ivory/95 backdrop-blur-sm border-b border-charcoal/5" data-id="element-519" data-mp="2jpssfqm0ee4c">
        <a href="/" aria-label="Fem Entity home" data-id="element-520" data-mp="2eomzbrofsoua">
          <img src="https://cdn.magicpatterns.com/uploads/xpYure8muF9bisffxXgVg2/fementity.png" alt="Fem Entity" className="h-8 md:h-10" data-id="element-521" data-mp="1r0c5h8h5rpfi" />
        </a>
        <div className="hidden md:flex space-x-8 text-sm font-medium text-charcoal/80" data-id="element-522" data-mp="20rgfruvw2m40">
          <a href="/" className="hover:text-purple-700 transition-colors" data-id="element-523" data-mp="1w0hxiprcbdy4">
            Home
          </a>
          <a href="/get-the-app" className="hover:text-purple-700 transition-colors" data-id="element-524" data-mp="18a43wut3x71f">
            Get the App
          </a>
          <a href="/practitioners" className="hover:text-purple-700 transition-colors" data-id="element-525" data-mp="139ggej4378by">
            Practitioners
          </a>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-526" data-mp="1wzgyeofw7853">
        {/* Title area */}
        <header className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-charcoal/10" data-id="element-527" data-mp="21sjt4zweonmv">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-700 mb-5" data-id="element-528" data-mp="26ps0e5g0n79c">
            {policyMeta.company}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-charcoal leading-[1.05] tracking-tight" data-id="element-529" data-mp="1a36t1fwsmbwm">
            {policyMeta.title}
          </h1>
          <p className="mt-6 max-w-[62ch] text-lg text-charcoal/70 font-light leading-relaxed" data-id="element-530" data-mp="1j0hbbl8131b1">
            {policyMeta.summary}
          </p>

          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-531" data-mp="1ny9046ddm7b0">
            <div className="flex items-baseline gap-2" data-id="element-532" data-mp="2uis1y151xvos">
              <dt className="text-charcoal/50" data-id="element-533" data-mp="2zez73rpmj35o">Version</dt>
              <dd className="font-medium text-charcoal" data-id="element-534" data-mp="2zhldvp4oxemu">
                {policyMeta.version.replace('Version ', '')}
              </dd>
            </div>
            <div className="flex items-baseline gap-2" data-id="element-535" data-mp="iun7ujrnwceq">
              <dt className="text-charcoal/50" data-id="element-536" data-mp="e2c8bi0u2smp">Effective</dt>
              <dd className="font-medium text-charcoal" data-id="element-537" data-mp="e0ss1rf6ghkx">September 2026</dd>
            </div>
          </dl>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-538" data-mp="91g5l7s7xciv">
            <a href={policyMeta.pdfUrl} target="_blank" rel="noopener noreferrer" className={metaLinkStyles} data-id="element-539" data-mp="53ifm2qmimef">
              <DownloadIcon aria-hidden="true" className="w-4 h-4" data-id="element-540" data-mp="3ra6wf5he16ng" />
              Download the PDF
            </a>
            <a href={`mailto:${policyMeta.email}`} className={metaLinkStyles} data-id="element-541" data-mp="3mbcdm3jf7i3c">
              <MailIcon aria-hidden="true" className="w-4 h-4" data-id="element-542" data-mp="1pi0s60isjbja" />
              {policyMeta.email}
            </a>
          </div>
        </header>

        {/* Index + body */}
        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14 xl:gap-20 pb-24 md:pb-32" data-id="element-543" data-mp="1sjn7oqr1jz7q">
          <PolicyContents sections={policySections} activeId={activeId} data-id="element-544" data-mp="1xi6yb4gkxz6f" />

          <article className="max-w-[68ch] pt-10 lg:pt-14 divide-y divide-charcoal/10" data-id="element-545" data-mp="1xg45ydryftyj">
            {policySections.map(section => <PolicySectionBody key={section.id} section={section} data-id="element-546" data-mp="1njhlvr7gd6xk" />)}

            <p className="pt-12 text-sm text-charcoal/50" data-id="element-547" data-mp="19scj4g8sfyj0">
              © {new Date().getFullYear()} Fem Entity Inc. All rights reserved.{' '}
              {policyMeta.version} — {policyMeta.effectiveDate}.
            </p>
          </article>
        </div>
      </main>

      <BackToTop data-id="element-548" data-mp="zsvem259vqom" />

      <SiteFooter currentPath="/privacy-policy" data-id="element-549" data-mp="14tjfrgn7tnwk" />
    </div>;
}